import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextHeadline, TextTitle3, TextLabel2, TextBody } from '@cbhq/cds-web/typography';
import { RemoteImage } from '@cbhq/cds-web/media';
import { memo } from 'react';
import { DisplayType, format } from '@/utils/numberFormatter';
import { useTokenizedEquitiesData } from './hooks/useTokenizedEquitiesData';

const TOP_MOVERS_TITLE = 'Top Movers';

type TopMoverItemProps = {
  label: string;
  symbol: string;
  imageUrl: string;
  priceUsd: number;
  change24h: number;
};

const TopMoverItem = memo(function TopMoverItem({
  label,
  symbol,
  imageUrl,
  priceUsd,
  change24h,
}: TopMoverItemProps) {
  const isPositive = change24h > 0;
  const changeColor = isPositive ? 'fgPositive' : 'fgNegative';
  const changeIcon = isPositive ? '↗' : '↘';

  return (
    <HStack gap={2} alignItems="center" padding={0} width="100%">
      <RemoteImage source={imageUrl} size="m" shape="circle" />
      <VStack gap={1} flexGrow={1}>
        <HStack alignItems="center" width="100%">
          <VStack gap={0} flexGrow={1}>
            <TextHeadline as="span" noWrap>
              {label}
            </TextHeadline>
            <TextLabel2 as="span" color="fgMuted" noWrap>
              {symbol}
            </TextLabel2>
          </VStack>
          <VStack gap={0} alignItems="flex-end">
            <TextBody as="span" noWrap>
              {format({ value: priceUsd, type: DisplayType.Fiat })}
            </TextBody>
            <TextLabel2 as="span" color={changeColor} noWrap>
              {changeIcon} {Math.abs(change24h)}%
            </TextLabel2>
          </VStack>
        </HStack>
      </VStack>
    </HStack>
  );
});

function TopMoverItemLoading({ label }: { label: string }) {
  return (
    <HStack gap={2} alignItems="center" padding={0} width="100%">
      <Fallback width={32} height={32} shape="circle" disableRandomRectWidth />
      <VStack gap={1} flexGrow={1}>
        <HStack alignItems="center" width="100%">
          <VStack gap={0.5} flexGrow={1}>
            <TextHeadline as="span" noWrap>
              {label}
            </TextHeadline>
            <Fallback width={40} height={14} disableRandomRectWidth />
          </VStack>
          <VStack gap={0.5} alignItems="flex-end">
            <Fallback width={60} height={20} disableRandomRectWidth />
            <Fallback width={40} height={14} disableRandomRectWidth />
          </VStack>
        </HStack>
      </VStack>
    </HStack>
  );
}

const TokenizedEquitiesTopMoversContent = memo(function TokenizedEquitiesTopMoversContent() {
  const { summary, isLoading } = useTokenizedEquitiesData();

  if (isLoading) {
    return (
      <VStack gap={3}>
        <TextTitle3 as="h1" testID="tokenized-equities-top-movers-title">
          {TOP_MOVERS_TITLE}
        </TextTitle3>
        <VStack gap={3} padding={0}>
          <TopMoverItemLoading label="Top Performance" />
          <TopMoverItemLoading label="Top Loser" />
        </VStack>
      </VStack>
    );
  }

  return (
    <VStack gap={3}>
      {/* Title */}
      <TextTitle3 as="h1" testID="tokenized-equities-top-movers-title">
        {TOP_MOVERS_TITLE}
      </TextTitle3>

      {/* Top Movers */}
      <VStack gap={3} padding={0}>
        <TopMoverItem
          label="Top Performance"
          symbol={summary.topPerformer.symbol}
          imageUrl={summary.topPerformer.imageUrl}
          priceUsd={summary.topPerformer.priceUsd}
          change24h={summary.topPerformer.change24h}
        />
        <TopMoverItem
          label="Top Loser"
          symbol={summary.topLoser.symbol}
          imageUrl={summary.topLoser.imageUrl}
          priceUsd={summary.topLoser.priceUsd}
          change24h={summary.topLoser.change24h}
        />
      </VStack>
    </VStack>
  );
});

export const TokenizedEquitiesTopMovers = memo(function TokenizedEquitiesTopMovers() {
  return (
    <VStack background="bg" borderRadius={300} padding={3}>
      <TokenizedEquitiesTopMoversContent />
    </VStack>
  );
});
